import { z } from 'zod';

/**
 * 企业标识：一套部署只有一个企业，这串大写英文是它在系统里的代号。
 * 首位必须字母，避免出现纯数字的标识——它会被当成 ID 用，纯数字容易和自增主键混淆。
 */
export const organizationCodeSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9]{1,11}$/, '2–12 位大写英文或数字，首位必须是字母');

/**
 * 系统对外的访问地址。前端默认取浏览器当前 origin，运维可改。
 * 本地化部署里服务端无从知道自己对外是什么地址，所以这个值必须存下来——
 * 邀请链接和密码重置入口都要用它拼绝对 URL。
 */
export const baseUrlSchema = z
  .string()
  .regex(/^https?:\/\/[^\s/]+(\/.*)?$/, '请填写完整地址，含 http:// 或 https://');

/** 选填的护栏：填了之后只能添加该域名下的邮箱。不是安全边界，只防手滑。 */
export const emailDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, '域名格式不对');

export const organizationNameSchema = z.string().trim().min(1, '请填写企业名称').max(120);
export const organizationShortNameSchema = z.string().trim().max(60);
export const timezoneSchema = z.string().trim().min(1, '请填写时区');

export const organizationInputSchema = z.object({
  name: organizationNameSchema,
  shortName: organizationShortNameSchema.optional(),
  code: organizationCodeSchema,
  baseUrl: baseUrlSchema,
  /**
   * 归日锚点（I-7、ADR-0051）。**必填，没有默认值。**
   *
   * 此前这里是 `.default('Asia/Shanghai')`——库层本来就没有默认值，契约层有一个，
   * 于是不填时区会被**代码替客户决定**归日口径。
   * 初始化时人必须选一个：**这件事本来就该是人的决定。**
   * 界面下拉的初值见 `TIMEZONE_PICKER_INITIAL`（那是光标落点，不是默认值）。
   */
  timezone: timezoneSchema,
  emailDomain: emailDomainSchema.optional(),
});

export type OrganizationInput = z.infer<typeof organizationInputSchema>;

export const organizationSchema = z.object({
  name: z.string(),
  shortName: z.string().nullable(),
  code: z.string(),
  baseUrl: z.string(),
  timezone: z.string(),
  emailDomain: z.string().nullable(),
});

export type Organization = z.infer<typeof organizationSchema>;

/**
 * 设置里能改的那些。
 *
 * **企业标识（code）不在里面，故意的**：它是实例身份（访问地址、对外称谓），
 * 改一次就是一次数据迁移，不是改个字段。C-198 起 ATC 编码不再由它派生
 * （ADR-0052：ATC-nn-nnn，部门段 + 序号）——不可改的理由从「编码链」变为「身份」。
 *
 * 简称和邮箱域名传空字符串表示清掉——它们本来就是选填的。
 */
export const updateOrganizationSchema = z.object({
  name: organizationNameSchema.optional(),
  shortName: z.union([organizationShortNameSchema, z.literal('')]).optional(),
  baseUrl: baseUrlSchema.optional(),
  timezone: timezoneSchema.optional(),
  emailDomain: z.union([emailDomainSchema, z.literal('')]).optional(),
});

export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;
